import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CaseworkerAbsenceModel } from '../../../../api-service/clients';
import { AreaContainer } from '../../../../components/area-layout/area-container';
import { AreaContent } from '../../../../components/area-layout';
import { ConfirmDialog } from '../../../../components/confirm-dialog/confirm-dialog';
import { CaseworkerAbsenceForm, IAbsenceForm } from './caseworker-absence-form';
import { CaseworkerAbsenceList } from './caseworker-absence-list';
import { formatAbsencePeriod } from './absence-day';

export interface ICaseworkerOption {
  id: string;
  name: string;
}

export interface IForumSettingsTabComponentProps {
  caseworkers: ICaseworkerOption[];
  selectedCaseworkerId: string;
  showCaseworkerPicker: boolean;
  onCaseworkerChange: (caseworkerId: string) => void;
  absences: CaseworkerAbsenceModel[];
  isLoading: boolean;
  hasFailed: boolean;
  isFormOpen: boolean;
  absenceToEdit?: CaseworkerAbsenceModel;
  selectedAbsenceIds: string[];
  isDeleteDialogOpen: boolean;
  isBusy: boolean;
  saveError?: string;
  onSave: (form: IAbsenceForm) => void;
  onCancelForm: () => void;
  onCreateAbsence: () => void;
  onOpenAbsence: (absence: CaseworkerAbsenceModel) => void;
  onSelectionChange: (absenceIds: string[]) => void;
  onRequestDelete: () => void;
  onConfirmDelete: () => void;
  onDeclineDelete: () => void;
}

export function ForumSettingsTabComponent(props: IForumSettingsTabComponentProps) {
  const {
    caseworkers,
    selectedCaseworkerId,
    showCaseworkerPicker,
    onCaseworkerChange,
    absences,
    isLoading,
    hasFailed,
    isFormOpen,
    absenceToEdit,
    selectedAbsenceIds,
    isDeleteDialogOpen,
    isBusy,
    saveError,
    onSave,
    onCancelForm,
    onCreateAbsence,
    onOpenAbsence,
    onSelectionChange,
    onRequestDelete,
    onConfirmDelete,
    onDeclineDelete,
  } = props;

  const selectedCaseworkerName = selectedCaseworkerId ? caseworkers.find(caseworker => caseworker.id === selectedCaseworkerId)?.name || 'Sagsbehandleren' : undefined;

  function handleCaseworkerChange(event: SelectChangeEvent) {
    onCaseworkerChange(event.target.value);
  }

  const caseworkerPicker = showCaseworkerPicker ? (
    <Select
      sx={{ height: '35px', marginRight: '5px' }}
      value={selectedCaseworkerId}
      disabled={isBusy}
      displayEmpty
      onChange={handleCaseworkerChange}
      SelectDisplayProps={{ 'aria-label': 'Sagsbehandler' }}
    >
      <MenuItem value="">Mig selv</MenuItem>
      {caseworkers.map(caseworker => (
        <MenuItem key={caseworker.id} value={caseworker.id}>
          {caseworker.name}
        </MenuItem>
      ))}
    </Select>
  ) : undefined;

  return (
    <AreaContainer>
      <AreaContent>
        <CaseworkerAbsenceList
          absences={absences}
          isLoading={isLoading}
          hasFailed={hasFailed}
          isBusy={isBusy}
          selectedAbsenceIds={selectedAbsenceIds}
          caseworkerPicker={caseworkerPicker}
          onSelectionChange={onSelectionChange}
          onCreate={onCreateAbsence}
          onOpen={onOpenAbsence}
          onDeleteSelected={onRequestDelete}
        />
      </AreaContent>
      <Dialog open={isFormOpen} onClose={onCancelForm} maxWidth="md" fullWidth aria-labelledby="absence-dialog-title">
        <DialogTitle id="absence-dialog-title">{absenceToEdit ? `Rediger fravær ${formatAbsencePeriod(absenceToEdit)}` : 'Registrér fravær'}</DialogTitle>
        <DialogContent>
          <Box paddingTop={1}>
            <CaseworkerAbsenceForm
              key={absenceToEdit?.absenceId ?? 'new'}
              absenceToEdit={absenceToEdit}
              absences={absences}
              caseworkerName={selectedCaseworkerName}
              isSaving={isBusy || isLoading}
              saveError={saveError}
              onSave={onSave}
              onCancel={onCancelForm}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        type="alert"
        title="Vil du slette de valgte fravær?"
        message={selectedAbsenceIds.length === 1 ? 'Det valgte fravær bliver fjernet.' : `De ${selectedAbsenceIds.length} valgte fravær bliver fjernet.`}
        confirmButtonText="Ja"
        cancelButtonText="Nej"
        onConfirmClick={onConfirmDelete}
        onCancelClick={onDeclineDelete}
      />
    </AreaContainer>
  );
}
